/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import configLoader from "@src/utils/Config";
import { ON_PREM_PROVIDER, TECH9IQ_MARKS } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";

/*
 * How the migration picture shows a platform: its mark (the Tech9IQ sprite for AWS, Azure,
 * Nutanix and IBM, the Server icon for On-prem, the vendor wordmark served by /api/logos for the
 * rest) and its brand colour, which tints the machine box.
 */

/** Each platform's brand colour, used at low opacity for the box tint and full for its outline. */
export const PLATFORM_COLOURS: { [provider: string]: string } = {
  vmware_vsphere: "#0079B8",
  nutanix: "#7855FA",
  aws: "#FF9900",
  azure: "#0078D4",
  "hyper-v": "#00A4EF",
  scvmm: "#00A4EF",
  openstack: "#ED1944",
  vhi: "#2F80ED",
  kubevirt: "#EE0000",
  rhev: "#EE0000",
  harvester: "#30BA78",
  libvirt: "#30BA78",
  sap_libvirt: "#30BA78",
  lxd: "#E95420",
  proxmox: "#E57000",
  oci: "#C74634",
  opc: "#C74634",
  opca: "#C74634",
  o3c: "#C74634",
  oracle_vm: "#C74634",
  olvm: "#C74634",
  cloudstack: "#2C8FDB",
  stackit: "#00A3A3",
  ibm: "#0F62FE",
  ibm_cloud: "#0F62FE",
  ibm_power: "#0F62FE",
  powervc: "#0F62FE",
};

/** The brand steel, for On-prem and any platform without a colour of its own. */
export const DEFAULT_PLATFORM_COLOUR = "var(--t9-brand-500)";

export const platformColour = (provider?: string | null) =>
  (provider && PLATFORM_COLOURS[provider]) || DEFAULT_PLATFORM_COLOUR;

export type PlatformMark =
  | { kind: "sprite"; id: string }
  | { kind: "onprem" }
  | { kind: "wordmark"; light: string; dark: string }
  | { kind: "none" };

/** Which mark to draw for a provider; "none" means its name only. */
export const platformMark = (provider?: string | null): PlatformMark => {
  if (!provider) {
    return { kind: "none" };
  }
  if (provider === ON_PREM_PROVIDER) {
    return { kind: "onprem" };
  }
  if (TECH9IQ_MARKS[provider]) {
    return { kind: "sprite", id: TECH9IQ_MARKS[provider] };
  }
  const names = (configLoader.config && configLoader.config.providerNames) || {};
  if (Object.prototype.hasOwnProperty.call(names, provider)) {
    return {
      kind: "wordmark",
      light: `/api/logos/${provider}/32`,
      dark: `/api/logos/${provider}/32/white`,
    };
  }
  return { kind: "none" };
};

/** The arc's palette, Tech9IQ brand: steel, Bright Cyan, Brand Green (fixed, they read in both themes). */
export const ARC_STOPS = ["#5B788A", "#46C4F2", "#71C173"] as const;

const hexRgb = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));

/** The arc's colour `f` (0..1) of the way from source to target, as "rgb(r, g, b)". */
export const arcColourAt = (f: number) => {
  const t = Math.max(0, Math.min(1, f)) * 2;
  const i = t < 1 ? 0 : 1;
  const k = t - i;
  const a = hexRgb(ARC_STOPS[i]);
  const b = hexRgb(ARC_STOPS[i + 1]);
  const c = a.map((v, j) => Math.round(v + (b[j] - v) * k));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
};
